import { useState, useEffect } from 'react';
import { Modal, Form, Input, Radio, message } from 'antd';
import { saveOrUpdatePosterFieldApi } from '@services/Selection';
import './index.less';

// 弹框表单控件布局
const formItemLayout = {
    labelCol: { span: 5 },
    wrapperCol: { span: 18 },
};

// 字段类型选项（builtIn: 0-自定义 1-团队成员 2-成果摘要）
const fieldTypeMap = [
    { value: 0, label: '自定义' },
    { value: 1, label: '团队成员' },
    { value: 2, label: '成果摘要' },
];

// 字段形式选项（type: 1-图文 2-文本）
const fieldFormTypeMap = [
    { value: 1, label: '图文' },
    { value: 2, label: '文本' },
];

// 是否必填选项
const requiredMap = [
    { value: 1, label: '必填' },
    { value: 0, label: '非必填' },
];

// 新增/编辑壁报字段弹窗
export default function PosterFieldModal({ open, onCancel, onSaveSuccess, editData, existingFields = [], projectId }) {
    const [form] = Form.useForm();
    const [isEdit, setIsEdit] = useState(false);
    const [loading, setLoading] = useState(false);

    // 使用 useWatch 监听 builtIn 字段变化，确保字段形式禁用状态实时更新
    const builtInValue = Form.useWatch('builtIn', form);

    // 初始化表单数据
    useEffect(() => {
        if (open) {
            if (editData) {
                setIsEdit(true);
                form.setFieldsValue(editData);
            } else {
                setIsEdit(false);
                // 新增时完全重置表单，确保所有状态恢复初始值
                form.resetFields();
                // 使用 setTimeout 确保 resetFields 完成后再设置默认值
                setTimeout(() => {
                    form.setFieldsValue({
                        builtIn: 0,
                        type: 1,
                        required: 1,
                    });
                    // 强制更新表单状态，确保字段形式禁用状态正确
                    form.validateFields(['builtIn'], { validateOnly: true });
                }, 0);
            }
        }
    }, [open, editData, form]);

    // 监听字段类型变化，联动字段形式
    const handleFieldTypeChange = e => {
        const value = e.target.value;
        // 如果字段类型是成果摘要(2)或团队成员(1)，则字段形式固定为文本(2)
        if (value === 1 || value === 2) {
            form.setFieldsValue({ type: 2 });
        }
    };

    // 判断字段形式是否禁用（团队成员和成果摘要固定为文本）
    const isFieldFormTypeDisabled = () => {
        // 使用 useWatch 监听的值，确保实时更新
        // 新增时 builtInValue 可能为 undefined，此时默认为 0（自定义），不禁用
        if (builtInValue === undefined) {
            return false;
        }
        return builtInValue === 1 || builtInValue === 2;
    };

    // 保存
    const handleOk = () => {
        form.validateFields().then(async values => {
            // 构建请求参数
            const params = {
                ...values,
                note: values.note || '',
                projectId,
            };

            // 编辑时传递 id
            if (isEdit && editData?.id) {
                params.id = editData.id;
            }

            setLoading(true);
            try {
                const res = await saveOrUpdatePosterFieldApi(params);
                if (res && res.status_code === 200) {
                    message.success(isEdit ? '编辑成功' : '新增成功');
                    onCancel(); // 关闭弹窗
                    onSaveSuccess?.(); // 通知父组件刷新列表
                }
            } catch (error) {
                console.error('保存壁报字段失败:', error);
            } finally {
                setLoading(false);
            }
        });
    };

    return (
        <Modal title={isEdit ? '编辑壁报字段' : '新增壁报字段'} open={open} onOk={handleOk} onCancel={onCancel} width={700} okText="保存" cancelText="取消" className="poster-field-modal" destroyOnHidden mask={{ closable: false }} confirmLoading={loading}>
            <Form form={form} {...formItemLayout}>
                <Form.Item name="builtIn" label="字段类型" rules={[{ required: true, message: '请选择字段类型' }]}>
                    <Radio.Group disabled={isEdit} onChange={handleFieldTypeChange}>
                        {fieldTypeMap.map(item => {
                            // 编辑模式下整体已禁用，无需单独处理
                            // 新增模式下，检查是否已存在该类型的字段（仅针对团队成员和成果摘要）
                            const isDisabled = !isEdit && (item.value === 1 || item.value === 2) && existingFields.some(field => field.builtIn === item.value);

                            return (
                                <Radio key={item.value} value={item.value} disabled={isEdit || isDisabled}>
                                    {item.label}
                                </Radio>
                            );
                        })}
                    </Radio.Group>
                </Form.Item>

                <Form.Item name="type" label="字段形式" rules={[{ required: true, message: '请选择字段形式' }]}>
                    <Radio.Group disabled={isFieldFormTypeDisabled()}>
                        {fieldFormTypeMap.map(item => (
                            <Radio key={item.value} value={item.value}>
                                {item.label}
                            </Radio>
                        ))}
                    </Radio.Group>
                </Form.Item>

                <Form.Item name="required" label="是否必填" rules={[{ required: true, message: '请选择是否必填' }]}>
                    <Radio.Group>
                        {requiredMap.map(item => (
                            <Radio key={item.value} value={item.value}>
                                {item.label}
                            </Radio>
                        ))}
                    </Radio.Group>
                </Form.Item>

                <Form.Item
                    name="name"
                    label="显示名称"
                    rules={[
                        { required: true, message: '请输入显示名称' },
                        { max: 10, message: '显示名称不能超过 10 个字符' },
                    ]}
                >
                    <Input placeholder="请输入显示名称" maxLength={10} showCount />
                </Form.Item>

                <Form.Item name="note" label="显示说明">
                    <Input.TextArea placeholder="请输入显示说明（选填）" maxLength={100} showCount rows={4} />
                </Form.Item>
            </Form>
        </Modal>
    );
}
